import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../../components/ui/Card';
import { useAuth } from '../../../context/AuthContext';
import { RefreshCw, Calendar } from 'lucide-react';
import AccountSwitcher from '../../../components/AccountSwitcher';
import { useAccounts } from '../../../context/AccountContext';
import DateRangeFilter from '../../../components/filters/DateRangeFilter';

const API_BASE = 'https://api.glowytics.com';
const DEMO_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';

const demoPayload = {
  totals: { spend: 1325000, revenue: 5840000, purchases: 920 },
  age: [
    { label: '18-24', spend: 185000, revenue: 720000, purchases: 130 },
    { label: '25-34', spend: 420000, revenue: 2050000, purchases: 320 },
    { label: '35-44', spend: 330000, revenue: 1690000, purchases: 250 },
    { label: '45-54', spend: 220000, revenue: 880000, purchases: 140 },
    { label: '55-64', spend: 140000, revenue: 410000, purchases: 60 },
    { label: '65+', spend: 30000, revenue: 90000, purchases: 20 }
  ],
  gender: [
    { label: 'female', spend: 710000, revenue: 3380000, purchases: 540 },
    { label: 'male', spend: 560000, revenue: 2280000, purchases: 350 },
    { label: 'unknown', spend: 55000, revenue: 180000, purchases: 30 }
  ],
  interestEstimated: {
    method: 'google-ads-user-interest',
    rows: [
      { label: 'In-market: Apparel & Accessories', spend: 240000, revenue: 1320000, purchases: 220 },
      { label: 'Affinity: Shopping Enthusiasts', spend: 210000, revenue: 980000, purchases: 165 },
      { label: 'In-market: Home & Garden', spend: 160000, revenue: 720000, purchases: 110 },
      { label: 'Affinity: Beauty & Wellness', spend: 130000, revenue: 510000, purchases: 88 },
      { label: 'In-market: Sports & Fitness', spend: 95000, revenue: 320000, purchases: 60 }
    ]
  }
};

const DATE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7', label: 'Last 7 Days' },
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' }
];

const formatCurrency = (value, currency) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0
    }).format(number);
  } catch (e) {
    return Math.round(number).toLocaleString('en-US');
  }
};

function SimpleTable({ title, subtitle, rows, emptyText, currency }) {
  const totals = (rows || []).reduce((acc, r) => ({
    spend: acc.spend + (Number(r.spend) || 0),
    revenue: acc.revenue + (Number(r.revenue) || 0),
    purchases: acc.purchases + (Number(r.purchases) || 0)
  }), { spend: 0, revenue: 0, purchases: 0 });
  const totalSpend = totals.spend || 1;
  const totalRevenue = totals.revenue || 1;
  const overallRoas = totals.spend > 0 ? (totals.revenue / totals.spend).toFixed(2) : '—';
  const overallCpa = totals.purchases > 0 ? formatCurrency(totals.spend / totals.purchases, currency) : '—';
  return (
    <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-6 bg-white border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {subtitle ? <p className="text-sm text-gray-500 mt-1">{subtitle}</p> : null}
      </div>
      <div className="bg-white">
        {!rows || rows.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">{emptyText}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Segment</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Spend</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ROAS</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPA</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Spend % / Rev %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => {
                  const sPct = (row.spend / totalSpend) * 100;
                  const rPct = ((row.revenue || 0) / totalRevenue) * 100;
                  const roas = row.spend > 0 ? ((row.revenue || 0) / row.spend).toFixed(2) : '—';
                  const cpa = row.purchases > 0 ? formatCurrency(row.spend / row.purchases, currency) : '—';
                  const revColor = (row.revenue || 0) > 0 ? 'text-emerald-700' : 'text-gray-400';
                  return (
                    <tr key={row.label}>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.label}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right tabular-nums">{formatCurrency(row.spend, currency)}</td>
                      <td className={`px-4 py-3 text-sm text-right tabular-nums ${revColor}`}>{formatCurrency(row.revenue || 0, currency)}</td>
                      <td className="px-4 py-3 text-sm text-purple-600 text-right tabular-nums">{roas !== '—' ? `${roas}x` : '—'}</td>
                      <td className="px-4 py-3 text-sm text-orange-600 text-right tabular-nums">{cpa}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2 min-w-[160px]">
                          <div className="flex-1 space-y-1">
                            <div className="h-1.5 bg-gray-100 rounded overflow-hidden">
                              <div className="h-full bg-blue-500" style={{ width: `${Math.max(sPct, 1)}%` }} />
                            </div>
                            <div className="h-1.5 bg-gray-100 rounded overflow-hidden">
                              <div className="h-full bg-emerald-500" style={{ width: `${Math.max(rPct, 1)}%` }} />
                            </div>
                          </div>
                          <div className="text-[10px] text-gray-500 tabular-nums w-20 text-right leading-tight">
                            <div className="text-blue-600">{sPct.toFixed(1)}%</div>
                            <div className="text-emerald-600">{rPct.toFixed(1)}%</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 border-t border-gray-200">
                <tr>
                  <td className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase">Total</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right tabular-nums">{formatCurrency(totals.spend, currency)}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-emerald-700 text-right tabular-nums">{formatCurrency(totals.revenue, currency)}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-purple-700 text-right tabular-nums">{overallRoas !== '—' ? `${overallRoas}x` : '—'}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-orange-700 text-right tabular-nums">{overallCpa}</td>
                  <td className="px-4 py-3 text-xs text-gray-400 text-right">
                    <span className="text-blue-600">Spend</span> / <span className="text-emerald-600">Revenue</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function GoogleAdsAudienceInsights() {
  const { selectedBrand } = useAuth();
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('GOOGLE_ADS');
  const [dateRange, setDateRange] = useState('last30');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [customerId, setCustomerId] = useState('');

  const formatDateYMD = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  useEffect(() => {
    const loadConnections = async () => {
      if (!selectedBrand?.id) return;
      try {
        const resp = await fetch(`${API_BASE}/api/auth/connections`, {
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
          credentials: 'include'
        });
        if (!resp.ok) return;
        const data = await resp.json().catch(() => null);
        let found = null;
        if (Array.isArray(data)) {
          found = data.find((item) => item?.platform === 'GOOGLE_ADS');
        } else if (data && typeof data === 'object') {
          found = data.GOOGLE_ADS || data.google_ads || null;
        }
        if (found?.customerId) {
          setCustomerId(String(found.customerId));
        }
      } catch (_) {
        // ignore connection errors
      }
    };
    loadConnections();
  }, [selectedBrand?.id]);

  const fetchInsights = async () => {
    if (!selectedBrand?.id) return;
    setLoading(true);
    setError('');
    try {
      if (selectedBrand.id === DEMO_BRAND_ID) {
        setPayload(demoPayload);
        setLastUpdated(new Date());
        return;
      }

      if (!customerId) {
        throw new Error('Customer ID is required to fetch Google Ads insights.');
      }

      if (dateRange === 'custom' && (!customStartDate || !customEndDate)) {
        throw new Error('Please select a start and end date.');
      }

      const url = new URL(`${API_BASE}/api/google-ads/audience-insights`);
      url.searchParams.set('dateRange', dateRange);
      url.searchParams.set('customerId', customerId);
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        url.searchParams.set('startDate', formatDateYMD(customStartDate));
        url.searchParams.set('endDate', formatDateYMD(customEndDate));
      }
      const resp = await fetch(url.toString(), {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id }
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d?.error || d?.details || `HTTP ${resp.status}`);
      }
      const data = await resp.json().catch(() => ({}));
      if (data?.success === false) {
        throw new Error(data?.error || 'Failed to load Google Ads audience insights');
      }
      setPayload({
        totals: data?.totals || { spend: 0, revenue: 0, purchases: 0 },
        age: Array.isArray(data?.age) ? data.age : [],
        gender: Array.isArray(data?.gender) ? data.gender : [],
        interestEstimated: data?.interestEstimated || { method: 'google-ads-user-interest', rows: [] }
      });
      setLastUpdated(new Date());
    } catch (e) {
      setError(e?.message || 'Failed to load Google Ads audience insights');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange === 'custom') {
      if (customStartDate && customEndDate) {
        fetchInsights();
      }
    } else {
      fetchInsights();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, dateRange, customStartDate, customEndDate, customerId, selectedAccountId]);

  const summary = useMemo(() => {
    const spend = payload?.totals?.spend || 0;
    const revenue = payload?.totals?.revenue || 0;
    const purchases = payload?.totals?.purchases || 0;
    return {
      spend,
      revenue,
      purchases,
      roas: spend > 0 ? revenue / spend : 0,
      cpa: purchases > 0 ? spend / purchases : 0
    };
  }, [payload]);
  const meta = payload?.meta || null;

  const currency = selectedBrand?.currency;

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        <div className="p-6 bg-white text-gray-900 border-b flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-50 flex items-center justify-center">
              <img src="/discovergoogleads.svg" alt="Google Ads" className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl tracking-tight">Google Ads Audience Insights</h1>
              <p className="text-sm text-gray-500">
                Spend, revenue, ROAS, and CPA by age, gender, and interest segments.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <AccountSwitcher platform="GOOGLE_ADS" />
            <input
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder="Customer ID (123-456-7890)"
              className="text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white"
            />
            <DateRangeFilter
              value={dateRange}
              customStart={customStartDate}
              customEnd={customEndDate}
              onChange={(range, start, end) => {
                setDateRange(range);
                setCustomStartDate(start);
                setCustomEndDate(end);
              }}
              presets={DATE_OPTIONS.map(o => o.value)}
            />
            <button
              type="button"
              onClick={fetchInsights}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-gray-200 hover:bg-gray-50 shadow-sm"
              disabled={loading}
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <div className="text-xs text-gray-500 whitespace-nowrap">
              {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : ''}
            </div>
          </div>
        </div>
        {error && (
          <div className="px-6 py-4 bg-red-50 text-sm text-red-600 border-b border-red-100">
            {error}
          </div>
        )}
        {!error && loading && (
          <div className="px-6 py-10 bg-white text-sm text-gray-500 text-center">Loading Google Ads audience insights...</div>
        )}
        {!error && !loading && meta && meta.ageCount === 0 && meta.genderCount === 0 && meta.interestCount === 0 && (
          <div className="px-6 py-4 bg-amber-50 text-sm text-amber-700 border-b border-amber-100">
            Google Ads returned no demographic or interest rows for this account/date range.
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4 rounded-2xl border border-gray-100 shadow-sm">
          <div className="text-xs text-gray-500">Total Spend</div>
          <div className="text-xl font-semibold text-gray-900 mt-1">{formatCurrency(summary.spend, currency)}</div>
        </Card>
        <Card className="p-4 rounded-2xl border border-gray-100 shadow-sm">
          <div className="text-xs text-gray-500">Total Revenue</div>
          <div className="text-xl font-semibold text-emerald-600 mt-1">{formatCurrency(summary.revenue, currency)}</div>
        </Card>
        <Card className="p-4 rounded-2xl border border-gray-100 shadow-sm">
          <div className="text-xs text-gray-500">ROAS</div>
          <div className="text-xl font-semibold text-purple-600 mt-1">{summary.roas > 0 ? `${summary.roas.toFixed(2)}x` : '—'}</div>
        </Card>
        <Card className="p-4 rounded-2xl border border-gray-100 shadow-sm">
          <div className="text-xs text-gray-500">CPA</div>
          <div className="text-xl font-semibold text-orange-600 mt-1">{summary.cpa > 0 ? formatCurrency(summary.cpa, currency) : '—'}</div>
        </Card>
        <Card className="p-4 rounded-2xl border border-gray-100 shadow-sm">
          <div className="text-xs text-gray-500">Purchases</div>
          <div className="text-xl font-semibold text-gray-900 mt-1">{Math.round(summary.purchases).toLocaleString('en-US')}</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SimpleTable
          title="Performance by Age"
          subtitle="Spend, revenue, ROAS & CPA per age bucket (age_range_view)"
          rows={payload?.age || []}
          emptyText="No age breakdown available."
          currency={currency}
        />
        <SimpleTable
          title="Performance by Gender"
          subtitle="Spend, revenue, ROAS & CPA per gender (gender_view)"
          rows={payload?.gender || []}
          emptyText="No gender breakdown available."
          currency={currency}
        />
      </div>

      <SimpleTable
        title="Performance by Interests"
        subtitle="Manually-targeted user-interest criteria (PMax/Smart campaigns won't appear here)"
        rows={payload?.interestEstimated?.rows || []}
        emptyText="No manually-targeted user-interest criteria found in this date range."
        currency={currency}
      />
    </div>
  );
}
